class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = PredictiveSearch.resultCache ||= new Map();
    this.cachedCounts = PredictiveSearch.countCache ||= new Map();
    this.requestSequence = 0;
    this.pendingQuery = null;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search')
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.quickSearchTrending = this.querySelectorAll('.quickSearchTrending .link');
    this.searchResultsWidget = this.getElementsByClassName('quickSearchResultsWidget')[0];

    this.setupEventListeners();
    this.quickSearchTrending.forEach(element => {
      element.addEventListener('click', this.quickSearchTrendingClick.bind(this));
    });
  }
  
  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    // Invalidate immediately, before SearchForm's existing 300ms debounce fires.
    this.input.addEventListener('input', () => {
      this.suppressPendingInput = false;
      this.cancelSearchRequest();
      this.removeAttribute('results');
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
  }

  handleResponse(data,limit) {
    let htmlMedia = document.createElement('div');
    htmlMedia.innerHTML = data;

    $(htmlMedia).find(`.product:nth-child(n+${Number(limit) + 1})`).remove();
    return $(htmlMedia).html();
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    if (this.suppressPendingInput) return;
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    this.suppressPendingInput = false;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return this.showTrendingAndProducts();

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }
  
  onKeyup(event) {
    if (event.code === 'Escape') { this.close(); this.hideTrendingAndProducts(); return; }
    if (!this.getQuery().length) {
      this.close(true);
      this.showTrendingAndProducts();
    };
    event.preventDefault();
    
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
  
    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';
    
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
    
  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }
  
  // Short-lived, bounded caches shared by desktop/mobile search on this page only.
  readSearchCache(cache, key) {
    const entry = cache.get(key);
    if (!entry) return undefined;
    if (entry.expires <= Date.now()) { cache.delete(key); return undefined; }
    return entry.value;
  }

  writeSearchCache(cache, key, value) {
    cache.delete(key);
    cache.set(key, { value, expires: Date.now() + 60000 });
    while (cache.size > 20) cache.delete(cache.keys().next().value);
  }

  cancelSearchRequest() {
    this.requestSequence++;
    this.abortController.abort();
    this.pendingQuery = null;
    this.pendingRequest = null;
  }

  isCurrentSearch(sequence, searchTerm) {
    return this.isConnected && sequence === this.requestSequence &&
      this.getQuery().toLowerCase() === searchTerm.toLowerCase();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().toLowerCase();
    if (!queryKey) return;
    if (this.pendingQuery === queryKey) return this.pendingRequest;
    this.cancelSearchRequest();
    const sequence = this.requestSequence;
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    this.setLiveRegionLoadingState();
    const cached = this.readSearchCache(this.cachedResults, queryKey);
    if (cached !== undefined) {
      this.renderSearchResults(cached);
      this.updateViewAllLink(searchTerm, sequence, signal);
      return;
    }

    this.pendingQuery = queryKey;
    const normalized = window.IUStoreSearch?.normalize(searchTerm);
    const exactQuery = normalized?.material || normalized?.associated;
    const requestUrl = exactQuery
      ? window.IUStoreSearch.url(searchTerm, routes.search_url).href + '&section_id=iu-material-search'
      : `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;
    this.pendingRequest = fetch(requestUrl, { signal })
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(text => {
        if (!this.isCurrentSearch(sequence, searchTerm)) return;
        const section = new DOMParser().parseFromString(text, 'text/html')
          .querySelector(exactQuery ? '#shopify-section-iu-material-search' : '#shopify-section-predictive-search');
        if (!section) throw new Error('Missing predictive search section');
        this.writeSearchCache(this.cachedResults, queryKey, section.innerHTML);
        this.renderSearchResults(section.innerHTML);
        this.updateViewAllLink(searchTerm, sequence, signal);
      })
      .catch(error => {
        if (error?.name === 'AbortError' || error?.code === 20 || !this.isCurrentSearch(sequence, searchTerm)) return;
        // The regular search form remains usable when suggestions are unavailable.
        this.close();
        this.showTrendingAndProducts();
      })
      .finally(() => {
        if (sequence !== this.requestSequence) return;
        this.pendingQuery = null;
        this.pendingRequest = null;
      });
    return this.pendingRequest;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    clearTimeout(this.statusTimer);
    this.statusTimer = setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
    if ((window.show_multiple_currencies && typeof Currency != 'undefined' && Currency.currentCurrency != shopCurrency) || window.show_auto_currency) {
      Currency.convertAll(window.shop_currency, $('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
    }
    window.sharedFunctions?.swapHoverVideoProductCard();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('[class^="header-navigation"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
  
  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.suppressPendingInput = true;
    this.cancelSearchRequest();
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 
    this.trendingAndProductsBlock.classList.add('is-show')
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 

    this.trendingAndProductsBlock.classList.remove('is-show')
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = this.contains(e.target), $target = e.target

    if (!isInModal || $target.closest('.header-search-popup-close') || $target.matches('.header-search-popup-close') || $target.closest('.header-search-close') || $target.matches('.header-search-close')) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open')
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open')
  }

  async updateViewAllLink(searchTerm, sequence, signal) {
    const link = this.querySelector('[data-qs-view-all-link]');
    if (!link) return;
    const url = window.IUStoreSearch?.url(searchTerm, routes.search_url).href || `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&options%5Bprefix%5D=last&type=product`;
    link.href = url;
    const label = link.getAttribute('data-iu-count-label');
    if (label) link.textContent = label.replace('__IU_COUNT__', '').replace(/\(\s*\)/, '').trim();
    const count = this.querySelector('[data-iu-search-count]')?.getAttribute('data-iu-search-count') || await this.getTotalResults(url, signal);
    if (count === null || !this.isCurrentSearch(sequence, searchTerm) || !link.isConnected || this.querySelector('[data-qs-view-all-link]') !== link) return;
    if (label) link.textContent = label.replace('__IU_COUNT__', count);
  }

  async getTotalResults(url, signal) {
    const cached = this.readSearchCache(this.cachedCounts, url);
    if (cached !== undefined) return cached;
    try {
      // Fetch just a tiny Liquid section, never the complete search results page.
      const response = await fetch(`${url}&section_id=iu-search-count`, { signal });
      if (!response.ok) return null;
      const text = await response.text();
      if (signal.aborted) return null;
      const count = new DOMParser().parseFromString(text, 'text/html')
        .querySelector('[data-iu-search-count]')?.getAttribute('data-iu-search-count');
      if (!/^\d+$/.test(count || '')) return null;
      this.writeSearchCache(this.cachedCounts, url, count);
      return count;
    } catch {
      // A missing count must not hide valid suggestions or block the View all link.
      return null;
    }
  }

  quickSearchTrendingClick(event) {
    window.location = event.currentTarget.getAttribute('href');
  }
}

customElements.define('predictive-search', PredictiveSearch);
